/**
 * @file EnhancedDebugTools.tsx
 * @description Advanced debugging tools with batch operations and enhanced visualization capabilities
 */

import { useState, useCallback } from "react";
import {
  ChevronDown,
  ChevronRight,
  Code,
  Eye,
  List,
  Plus,
  Trash2,
} from "lucide-react";
import { useViewerStore } from "@/store/viewerStore";
import { PropertiesService } from "@/services/core/propertiesTools";
import { ViewerService } from "@/services/core/viewerTools";
import { toast } from "@/components/common/Toast";

interface ColorOption {
  label: string;
  value: THREE.Vector4;
  hex: string;
}

interface ElementToHighlight {
  id: string;
  inputValue: string;
  dbId: number | null;
  colorIndex: number;
}

export function EnhancedDebugTools() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [elements, setElements] = useState<ElementToHighlight[]>([
    { id: "1", inputValue: "", dbId: null, colorIndex: 0 },
    { id: "2", inputValue: "", dbId: null, colorIndex: 1 },
  ]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDifferentCategories, setShowDifferentCategories] = useState(false);
  const [includeNearbyContext, setIncludeNearbyContext] = useState(false);
  const [useJsonInput, setUseJsonInput] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  const { viewer, isModelLoaded } = useViewerStore();

  const JSON_PLACEHOLDER = `[
  { "dbId": 123456, "color": "Red" },
  { "dbId": 654321, "color": "Green" },
  { "dbId": "1234567", "color": "Blue" }
]`;

  // Helper function to get Forge Viewer's internal THREE.js instance
  const getTHREE = useCallback(() => {
    if (!viewer) return null;
    return (window as any).THREE || (viewer as any).impl?.renderer?.THREE;
  }, [viewer]);

  // Create color options using Forge Viewer's THREE.js instance
  const getColorOptions = useCallback((): ColorOption[] => {
    const THREE = getTHREE();
    if (!THREE) return [];

    return [
      { label: "Red", value: new THREE.Vector4(1, 0, 0, 0.6), hex: "#FF0000" },
      {
        label: "Green",
        value: new THREE.Vector4(0, 1, 0, 0.6),
        hex: "#00FF00",
      },
      { label: "Blue", value: new THREE.Vector4(0, 0, 1, 0.6), hex: "#0000FF" },
      {
        label: "Yellow",
        value: new THREE.Vector4(1, 1, 0, 0.6),
        hex: "#FFFF00",
      },
      {
        label: "Purple",
        value: new THREE.Vector4(0.5, 0, 1, 0.6),
        hex: "#8000FF",
      },
      { label: "Cyan", value: new THREE.Vector4(0, 1, 1, 0.6), hex: "#00FFFF" },
      {
        label: "Orange",
        value: new THREE.Vector4(1, 0.5, 0, 0.6),
        hex: "#FF8000",
      },
      {
        label: "Pink",
        value: new THREE.Vector4(1, 0.5, 0.8, 0.6),
        hex: "#FF80CC",
      },
    ];
  }, [getTHREE]);

  const addElement = () => {
    const newElement: ElementToHighlight = {
      id: Date.now().toString(),
      inputValue: "",
      dbId: null,
      colorIndex: elements.length % getColorOptions().length,
    };
    setElements([...elements, newElement]);
  };

  const removeElement = (id: string) => {
    if (elements.length > 1) {
      setElements(elements.filter((el) => el.id !== id));
    }
  };

  const updateElement = (
    id: string,
    field: keyof ElementToHighlight,
    value: any
  ) => {
    setElements(
      elements.map((el) => (el.id === id ? { ...el, [field]: value } : el))
    );
  };

  // ---------- JSON input helpers ----------

  const serializeElementsToJson = useCallback(
    (els: ElementToHighlight[]): string => {
      const palette = getColorOptions();
      const entries = els
        .filter((el) => el.inputValue.trim())
        .map((el) => {
          const raw = el.inputValue.trim();
          const num = Number(raw);
          const dbId = !isNaN(num) && /^\d+$/.test(raw) ? num : raw;
          return {
            dbId,
            color: palette[el.colorIndex]?.label ?? "Red",
          };
        });
      return JSON.stringify(entries, null, 2);
    },
    [getColorOptions]
  );

  const parseJsonInput = useCallback(
    (
      text: string
    ): { entries: ElementToHighlight[] } | { error: string } => {
      const trimmed = text.trim();
      if (!trimmed) return { entries: [] };

      let parsed: any;
      try {
        parsed = JSON.parse(trimmed);
      } catch (e: any) {
        return { error: `Invalid JSON: ${e.message}` };
      }

      if (!Array.isArray(parsed)) {
        return { error: "Expected a JSON array of { dbId, color } objects" };
      }

      const palette = getColorOptions();
      const colorByLabel = new Map<string, number>();
      palette.forEach((c, i) => colorByLabel.set(c.label.toLowerCase(), i));
      const validNames = palette.map((c) => c.label).join("/");

      const entries: ElementToHighlight[] = [];
      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];
        if (!item || typeof item !== "object") {
          return { error: `Item ${i + 1}: must be an object` };
        }
        if (item.dbId === undefined || item.dbId === null || item.dbId === "") {
          return { error: `Item ${i + 1}: missing "dbId"` };
        }
        if (typeof item.color !== "string") {
          return {
            error: `Item ${i + 1}: missing or invalid "color" — use ${validNames}`,
          };
        }
        const colorIndex = colorByLabel.get(item.color.toLowerCase());
        if (colorIndex === undefined) {
          return {
            error: `Item ${i + 1}: unknown color "${item.color}" — use ${validNames}`,
          };
        }
        entries.push({
          id: `${Date.now()}-${i}`,
          inputValue: String(item.dbId),
          dbId: null,
          colorIndex,
        });
      }
      return { entries };
    },
    [getColorOptions]
  );

  // Toggle between row mode and JSON mode, syncing both directions.
  const toggleInputMode = (toJson: boolean) => {
    if (toJson === useJsonInput) return;

    if (toJson) {
      // row → JSON: serialize current rows
      setJsonText(serializeElementsToJson(elements));
      setJsonError(null);
      setUseJsonInput(true);
      return;
    }

    // JSON → row: parse, refuse switch on error
    const result = parseJsonInput(jsonText);
    if ("error" in result) {
      setJsonError(result.error);
      toast.error(result.error);
      return;
    }
    if (result.entries.length === 0) {
      setElements([
        { id: "1", inputValue: "", dbId: null, colorIndex: 0 },
      ]);
    } else {
      setElements(result.entries);
    }
    setJsonError(null);
    setUseJsonInput(false);
  };

  // Returns the elements array to use for highlighting, or null if JSON is invalid.
  const getElementsForHighlight = (): ElementToHighlight[] | null => {
    if (!useJsonInput) return elements;
    const result = parseJsonInput(jsonText);
    if ("error" in result) {
      setJsonError(result.error);
      toast.error(result.error);
      return null;
    }
    setJsonError(null);
    setElements(
      result.entries.length > 0
        ? result.entries
        : [{ id: "1", inputValue: "", dbId: null, colorIndex: 0 }]
    );
    return result.entries;
  };

  const handleHighlight = async () => {
    if (!viewer || !isModelLoaded) {
      toast.error("Viewer not ready. Please load a model first.");
      return;
    }

    const activeElements = getElementsForHighlight();
    if (activeElements === null) return; // JSON parse error already surfaced

    const hasInput = activeElements.some((el) => el.inputValue.trim());
    if (!hasInput) {
      toast.warning("Please enter at least one Element ID");
      return;
    }

    setIsProcessing(true);

    try {
      // Clear any previous highlights and, crucially, turn off Forge's ghost
      // render pass so isolated elements aren't surrounded by translucent
      // wireframes of the rest of the model.
      ViewerService.prepareCleanIsolation(viewer);
      viewer.isolate([]); // Show all elements
      viewer.showAll();

      const validDbIds: number[] = [];
      const elementResults: Array<{
        dbId: number;
        color: THREE.Vector4;
        name: string;
      }> = [];
      const invalidIds: string[] = [];

      // Process each element
      for (const element of activeElements) {
        if (!element.inputValue.trim()) continue;

        const id = element.inputValue.trim();

        let dbId: number | null = null;

        // Try to parse as number (DbId)
        const numId = parseInt(id);
        if (!isNaN(numId)) {
          dbId = numId;
        } else {
          // Try as Revit Element ID
          dbId = await PropertiesService.findDbIdByRevitId(viewer, id);
          if (dbId) {
          } else {
            invalidIds.push(id);
          }
        }

        if (dbId) {
          validDbIds.push(dbId);
          updateElement(element.id, "dbId", dbId);

          // Get element name for logging
          try {
            const props = await PropertiesService.getElementProperties(
              viewer,
              dbId
            );
            const nameProp = props.find(
              (p) =>
                p.displayName === "Name" || p.displayName === "Element Name"
            );
            const name = nameProp?.displayValue || `Element_${dbId}`;

            elementResults.push({
              dbId,
              color: getColorOptions()[element.colorIndex].value,
              name: name as string,
            });
          } catch (e) {
            elementResults.push({
              dbId,
              color: getColorOptions()[element.colorIndex].value,
              name: `Element_${dbId}`,
            });
          }
        }
      }

      // Report invalid IDs
      if (invalidIds.length > 0) {
        toast.warning(`Could not find elements: ${invalidIds.join(", ")}`);
      }

      // Highlight valid elements with their individual colors
      if (elementResults.length > 0) {

        // Apply colors to each element
        elementResults.forEach(({ dbId, color, name }) => {
          viewer.setThemingColor(dbId, color);
        });

        // Optionally isolate elements if setting is enabled. Context elements
        // (nearby) render at partial opacity so the user-selected dbIds stay
        // unobstructed; ceilings near-invisible to keep top-down views clear.
        if (!showDifferentCategories) {
          let idsToIsolate = validDbIds;
          if (includeNearbyContext) {
            const raw = ViewerService.findNearbyElements(viewer, validDbIds);
            idsToIsolate = ViewerService.filterByFloorBand(viewer, raw, validDbIds, 2);
          }
          await ViewerService.isolateWithTransparentCeilings(
            viewer,
            idsToIsolate,
            { focusedIds: validDbIds }
          );
        }

        // Fit view to show all highlighted elements
        viewer.fitToView(validDbIds, viewer.model, true);

        // Force viewer update
        viewer.impl.invalidate(true, true, true);

        const colorSummary = elementResults
          .map(
            (r) =>
              `${r.name} (${getColorOptions().find((c) => c.value === r.color)?.label})`
          )
          .join(", ");

        toast.success(`Highlighted ${elementResults.length} element(s)`);

      } else {
        toast.error("No valid elements found to highlight");
      }
    } catch (error) {
      toast.error("Error highlighting elements");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearHighlights = () => {
    if (!viewer) {
      toast.error("Viewer not ready");
      return;
    }


    // Clear all theming colors
    viewer.clearThemingColors();

    // Reset ceiling transparency applied by isolateWithTransparentCeilings
    ViewerService.resetTransparentFragments(viewer);

    // Drop any slice plane set by sliceAtFloorHeight
    try { viewer.setCutPlanes([]); } catch { /* noop */ }

    // Show all elements
    viewer.isolate([]);
    viewer.showAll();

    // Force viewer update
    viewer.impl.invalidate(true, true, true);

    // Reset element values
    setElements(elements.map((el) => ({ ...el, inputValue: "", dbId: null })));

    toast.info("All highlights cleared");
  };

  return (
    <div className="mt-4 bg-dark-100 rounded-lg border border-gray-700">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-dark-200 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-purple-400" />
          <span className="font-semibold text-gray-100">
            Multi-Element Visualizer
          </span>
          <span className="text-xs text-white"></span>
        </div>
        <div className="flex items-center gap-2">
          {!isCollapsed && (
            <span className="text-xs text-white">
              {isModelLoaded ? "✓ Model Loaded" : "⚠ No Model"}
            </span>
          )}
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5 text-white" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white" />
          )}
        </div>
      </button>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 border-t border-gray-700">
          {/* Multi-Element Visualizer Section */}
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Multi-Element Visualizer
            </h3>

            <div className="space-y-3">
              {/* Settings */}
              <div className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showDifferentCategories}
                  onChange={(e) => setShowDifferentCategories(e.target.checked)}
                  className="w-4 h-4"
                  id="showCategories"
                />
                <label htmlFor="showCategories" className="text-gray-300">
                  Show all model elements (not just highlighted)
                </label>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeNearbyContext}
                  onChange={(e) => setIncludeNearbyContext(e.target.checked)}
                  disabled={showDifferentCategories}
                  className="w-4 h-4"
                  id="includeNearby"
                />
                <label
                  htmlFor="includeNearby"
                  className={
                    showDifferentCategories
                      ? "text-gray-500"
                      : "text-gray-300"
                  }
                  title="When isolating, also show surrounding elements found via padded bounding-box (PDF-style)"
                >
                  Include nearby context when isolating (PDF-style)
                </label>
              </div>

              {/* Input Mode Toggle */}
              <div className="flex items-center gap-1 bg-dark-200 rounded p-1 w-fit">
                <button
                  onClick={() => toggleInputMode(false)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                    !useJsonInput
                      ? "bg-purple-600 text-white"
                      : "text-gray-400 hover:text-gray-200"
                  }`}
                  title="One row per element"
                >
                  <List className="w-3 h-3" />
                  Rows
                </button>
                <button
                  onClick={() => toggleInputMode(true)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                    useJsonInput
                      ? "bg-purple-600 text-white"
                      : "text-gray-400 hover:text-gray-200"
                  }`}
                  title="Paste a JSON array of { dbId, color }"
                >
                  <Code className="w-3 h-3" />
                  JSON
                </button>
              </div>

              {useJsonInput ? (
                /* JSON Bulk Input */
                <div className="space-y-2">
                  <textarea
                    value={jsonText}
                    onChange={(e) => {
                      setJsonText(e.target.value);
                      if (jsonError) setJsonError(null);
                    }}
                    placeholder={JSON_PLACEHOLDER}
                    rows={10}
                    spellCheck={false}
                    className="w-full px-3 py-2 bg-dark-200 text-gray-100 rounded border border-gray-600 focus:border-blue-500 focus:outline-none text-xs font-mono resize-y"
                  />
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => {
                        setJsonText(JSON_PLACEHOLDER);
                        setJsonError(null);
                      }}
                      className="text-xs text-blue-400 hover:text-blue-300 underline"
                    >
                      Load example
                    </button>
                    <span className="text-xs text-gray-500">
                      Colors: Red, Green, Blue, Yellow, Purple, Cyan, Orange,
                      Pink
                    </span>
                  </div>
                  {jsonError && (
                    <div className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded px-2 py-1">
                      {jsonError}
                    </div>
                  )}
                </div>
              ) : (
                <>
              {/* Element Input Fields */}
              <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                {elements.map((element, index) => (
                  <div key={element.id} className="flex items-center gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={element.inputValue}
                        onChange={(e) =>
                          updateElement(
                            element.id,
                            "inputValue",
                            e.target.value
                          )
                        }
                        placeholder={`Element ${index + 1} (DbId or RevitId)`}
                        className="w-full px-3 py-2 bg-dark-200 text-gray-100 rounded border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                      />
                    </div>

                    {/* Color Selector */}
                    <select
                      value={element.colorIndex}
                      onChange={(e) =>
                        updateElement(
                          element.id,
                          "colorIndex",
                          parseInt(e.target.value)
                        )
                      }
                      className="px-2 py-2 bg-dark-200 text-gray-100 rounded border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                      style={{
                        backgroundColor:
                          getColorOptions()[element.colorIndex].hex + "33",
                        borderColor: getColorOptions()[element.colorIndex].hex,
                      }}
                    >
                      {getColorOptions().map((color, idx) => (
                        <option
                          key={idx}
                          value={idx}
                          style={{ backgroundColor: "#1a1a1a" }}
                        >
                          {color.label}
                        </option>
                      ))}
                    </select>

                    {/* Remove Button */}
                    {elements.length > 1 && (
                      <button
                        onClick={() => removeElement(element.id)}
                        className="p-2 text-white hover:text-red-400 transition-colors"
                        title="Remove element"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}

                    {/* Status Indicator */}
                    {element.dbId && (
                      <span
                        className="text-xs text-green-400"
                        title={`DbId: ${element.dbId}`}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Element Button */}
              <button
                onClick={addElement}
                className="flex items-center gap-2 px-3 py-1 bg-dark-200 hover:bg-dark-300 text-gray-300 rounded transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Element
              </button>
                </>
              )}

              {/* Color Legend */}
              <div className="flex flex-wrap gap-1">
                {getColorOptions().map((color, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-1 px-2 py-1 bg-dark-200 rounded text-xs"
                  >
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: color.hex }}
                    />
                    <span className="text-white">{color.label}</span>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleHighlight}
                  disabled={isProcessing || !isModelLoaded}
                  className={`flex-1 px-4 py-2 rounded font-medium transition-colors ${
                    isProcessing || !isModelLoaded
                      ? "bg-gray-600 text-white cursor-not-allowed"
                      : "bg-purple-600 hover:bg-purple-700 text-white"
                  }`}
                >
                  {isProcessing ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">⟳</span>
                      Processing...
                    </span>
                  ) : (
                    "Highlight All Elements"
                  )}
                </button>

                <button
                  onClick={handleClearHighlights}
                  disabled={!isModelLoaded}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear All
                </button>
              </div>
            </div>

            {/* Line Thickness Test Section */}
            <div className="mt-3 p-3 bg-gray-100 rounded border border-gray-300">
              <div className="text-sm font-semibold text-gray-800 mb-2">
                Line Thickness Test (15x)
              </div>
              <div className="flex gap-2 mb-2">
                <button
                  onClick={async () => {
                    if (!viewer) {
                      toast.error("Viewer not ready");
                      return;
                    }
                    const { LineThicknessTools } = await import(
                      "@/services/tools/lineThicknessTools"
                    );
                    const success = LineThicknessTools.setLineThickness(viewer, 15);
                    if (success) {
                      toast.success("Line thickness set to 15x");
                    } else {
                      toast.warning("Line thickness API not available - may use fallback");
                    }
                  }}
                  disabled={!isModelLoaded}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply 15x Thickness
                </button>
                <button
                  onClick={async () => {
                    if (!viewer) {
                      toast.error("Viewer not ready");
                      return;
                    }
                    const { LineThicknessTools } = await import(
                      "@/services/tools/lineThicknessTools"
                    );
                    LineThicknessTools.resetLineThickness(viewer);
                    toast.info("Line thickness reset to default");
                  }}
                  disabled={!isModelLoaded}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reset
                </button>
                <button
                  onClick={async () => {
                    if (!viewer) {
                      toast.error("Viewer not ready");
                      return;
                    }
                    const { LineThicknessTools } = await import(
                      "@/services/tools/lineThicknessTools"
                    );
                    const results = LineThicknessTools.testAvailableMethods(viewer);
                    console.log("Line Thickness Methods Test:", results);
                    toast.info(
                      `Methods: API=${results.setLineThicknessAPI ? "✓" : "✗"} Renderer=${results.rendererSettings ? "✓" : "✗"} THREE=${results.threeLineWidth ? "✓" : "✗"}`,
                      { duration: 5000 }
                    );
                  }}
                  disabled={!isModelLoaded}
                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Test Methods
                </button>
              </div>
              <div className="text-xs text-gray-600">
                Tests multiple approaches to make model lines 15x thicker. Check console for details.
              </div>
            </div>

            {/* Debug Info */}
            <div className="mt-3 p-2 bg-dark-200 rounded text-xs text-white">
              <div>
                Viewer Status: {viewer ? "✓ Initialized" : "✗ Not Ready"}
              </div>
              <div>
                Model Status: {isModelLoaded ? "✓ Loaded" : "✗ Not Loaded"}
              </div>
              {viewer && (
                <>
                  <div>Model ID: {viewer.model?.id || "None"}</div>
                  <div>
                    Elements to Highlight:{" "}
                    {elements.filter((e) => e.inputValue.trim()).length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
